import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STATUT, relDate, exactDate, rdvFmt, ApercuPatient } from "@/pages/Patients";
import { useCreerRdv } from "@/components/CreerRdv";
import { MessageCircle, CalendarPlus, Calendar, Pencil, Phone } from "lucide-react";

const COLS = [
  { key: "a_relancer", label: "À relancer", hint: "facture non soldée", dot: "oklch(0.72 0.14 65)", btn: "Envoyer un rappel", Icon: MessageCircle, kind: "dark" },
  { key: "a_cloturer", label: "À clôturer", hint: "soin à documenter", dot: "oklch(0.7 0.15 75)", btn: "Ouvrir la fiche", Icon: Pencil, kind: "warm" },
  { key: "a_planifier", label: "À suivre", hint: "en retard ou cette semaine", dot: "oklch(0.62 0.13 255)", btn: "Planifier", Icon: CalendarPlus, kind: "yellow" },
  { key: "rdv_avenir", label: "RDV à venir", hint: "planifiés", dot: "oklch(0.62 0.13 155)", btn: "Voir le RDV", Icon: Calendar, kind: "warm" },
  { key: "nouveau", label: "Nouveau", hint: "0 contact", dot: "oklch(0.62 0.13 300)", btn: "Compléter la fiche", Icon: Pencil, kind: "violet" },
];

const btnStyle = (kind) => ({
  dark: { background: "var(--ink)", color: "#fff" },
  yellow: { background: "var(--accent)", color: "var(--ink)" },
  warm: { background: "var(--bg-warm)", color: "var(--text-2)" },
  violet: { background: "oklch(0.96 0.025 300)", color: "oklch(0.45 0.12 300)" },
}[kind]);

const ctxLine = (key, p) => {
  if (key === "a_relancer") return `Facture non soldée${p.solde_du ? " · " + Math.round(p.solde_du) + " €" : ""}`;
  if (key === "a_cloturer") return "Soin à documenter";
  if (key === "a_planifier") return p.prochain_suivi ? `Suivi ${exactDate(p.prochain_suivi)}${p.prochain_suivi_objet ? " · " + p.prochain_suivi_objet : ""}` : "À planifier";
  if (key === "rdv_avenir") return `RDV ${p.prochain_rdv ? rdvFmt(p.prochain_rdv) : "—"}`;
  return "Fiche incomplète · aucun contact";
};

export default function PatientsSuivi() {
  const nav = useNavigate();
  const { openCreerRdv } = useCreerRdv();
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [apercu, setApercu] = useState(null);
  const ctmapRef = useRef({});

  useEffect(() => {
    api.get("/patients").then((r) => setItems(r.data)).catch(() => {});
    api.get("/settings/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => { ctmapRef.current = Object.fromEntries((r.data || []).map((c) => [c.id, c.nom])); }).catch(() => {});
  }, []);

  const byCol = useMemo(() => {
    const m = { a_relancer: [], a_cloturer: [], a_planifier: [], rdv_avenir: [], nouveau: [] };
    items.forEach((p) => { if (m[p.statut]) m[p.statut].push(p); });
    // Règles de tri explicites par colonne
    m.a_relancer.sort((a, b) => (a.dernier_contact || "") < (b.dernier_contact || "") ? -1 : 1); // relances les plus anciennes d'abord
    // Suivis thérapeutiques : d'abord les suivis de la semaine courante, puis les retards en dessous
    const _today = new Date(); _today.setHours(0, 0, 0, 0);
    const _weekEnd = new Date(_today); _weekEnd.setDate(_today.getDate() + (6 - ((_today.getDay() + 6) % 7))); _weekEnd.setHours(23, 59, 59, 999);
    const _rank = (p) => { if (!p.prochain_suivi) return 3; const d = new Date(p.prochain_suivi); if (d < _today) return 1; if (d <= _weekEnd) return 0; return 2; };
    m.a_planifier.sort((a, b) => { const ra = _rank(a), rb = _rank(b); if (ra !== rb) return ra - rb; return (a.prochain_suivi || "") < (b.prochain_suivi || "") ? -1 : 1; });
    m.rdv_avenir.sort((a, b) => (a.prochain_rdv || "") < (b.prochain_rdv || "") ? -1 : 1); // RDV le plus proche d'abord
    m.nouveau.sort((a, b) => (a.created_at || "") > (b.created_at || "") ? -1 : 1); // plus récents d'abord
    return m;
  }, [items]);

  const nbRelancer = byCol.a_relancer.length;

  const action = (key, id) => {
    if (key === "a_relancer") nav(`/relation?patient=${id}`);
    else if (key === "a_cloturer") nav(`/patients/${id}`);
    else if (key === "a_planifier") openCreerRdv({ patientId: id });
    else if (key === "rdv_avenir") nav("/agenda");
    else nav(`/patients/${id}`);
  };

  return (
    <div className="-m-4 lg:-m-7 p-4 lg:p-7 min-h-[calc(100vh-120px)]" style={{ background: "var(--bg-warm)" }}>
      <h2 className="text-[20px] font-bold tracking-[-0.01em] mb-4" style={{ color: "var(--ink)" }}>
        {items.length} patients, <span className="sx-hl">{nbRelancer} à relancer</span>
      </h2>

      <div className="flex gap-4 items-start">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5 min-w-0">
          {COLS.map((c) => (
            <div key={c.key} data-testid={`suivi-col-${c.key}`} className="min-w-0">
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2 h-2 rounded-full" style={{ background: c.dot }} />
                <span className="text-[13.5px] font-semibold" style={{ color: "var(--ink)" }}>{c.label}</span>
                <span className="mono text-[11.5px]" style={{ color: "var(--muted-2)" }}>{byCol[c.key].length}</span>
                <span className="ml-auto text-[11.5px]" style={{ color: "var(--muted-2)" }}>{c.hint}</span>
              </div>
              <div className="space-y-2.5 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 240px)" }}>
                {byCol[c.key].length === 0 && <div className="text-[12.5px] px-1" style={{ color: "var(--faint)" }}>Aucun patient.</div>}
                {byCol[c.key].map((p) => (
                  <div key={p.id} data-testid={`suivi-card-${p.id}`} onClick={() => setApercu(p.id)}
                    className="bg-white rounded-xl p-3 cursor-pointer transition-shadow"
                    style={{ boxShadow: "0 1px 0 #e6e3da", borderTop: `3px solid ${c.dot}` }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "var(--sh-card-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 0 #e6e3da")}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[13.5px] font-semibold min-w-0 truncate" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</div>
                      <div className="flex flex-wrap gap-1 justify-end">
                        {(p.tags || []).slice(0, 2).map((tid) => { const t = tags.find((x) => x.id === tid); return t ? <span key={tid} className="sx-tag" style={{ padding: "1px 6px", fontSize: "10px" }}>{t.titre}</span> : null; })}
                      </div>
                    </div>
                    <div className="text-[12px] mt-1" style={{ color: "var(--muted)" }}>{ctxLine(c.key, p)}</div>
                    <div className="flex items-center gap-1.5 mt-2.5" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`suivi-act-${p.id}`} onClick={() => action(c.key, p.id)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-[8px] text-[12.5px] font-semibold" style={{ height: 30, ...btnStyle(c.kind) }}>
                        <c.Icon className="w-[14px] h-[14px]" />{c.btn}
                      </button>
                      <a href={p.telephone ? `tel:${p.telephone}` : undefined} onClick={(e) => e.stopPropagation()} title="Appeler"
                        className="inline-flex items-center justify-center rounded-[8px]" style={{ width: 30, height: 30, border: "1px solid var(--border-input)", background: "#fff", opacity: p.telephone ? 1 : 0.4 }}>
                        <Phone className="w-4 h-4" style={{ color: "var(--muted)" }} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {apercu && <ApercuPatient key={apercu} pid={apercu} onClose={() => setApercu(null)} nav={nav} ctmap={ctmapRef.current} tags={tags} secteurs={secteurs} />}
      </div>
    </div>
  );
}
